import React from 'react';
import { isChunkLoadError } from './chunkLoadError';
import { recordRenderThrow } from './renderThrowRecord';

interface Props {
  children: React.ReactNode;
}

interface State {
  /**
   * null = no error; Error = caught error. Kept in state so render()
   * can inspect it and re-throw non-chunk errors to a parent boundary.
   */
  caughtError: Error | null;
}

class ChunkLoadErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { caughtError: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Recorded HERE, not only in componentDidCatch: when render() below re-throws
    // a non-chunk error the commit phase never happens, so componentDidCatch never
    // fires for exactly the case that unmounts the tree and blanks #root. This is
    // the only hook guaranteed to run on that path.
    // Structured data only — still no console output.
    recordRenderThrow('ChunkLoadErrorBoundary', error);
    return { caughtError: error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Still NO console.error — the zero-tolerance console policy is intact.
    // This call does not duplicate the getDerivedStateFromError record; it
    // enriches it with the React component stack, which only this hook carries.
    recordRenderThrow('ChunkLoadErrorBoundary', error, errorInfo?.componentStack);
  }

  render(): React.ReactNode {
    const { caughtError } = this.state;

    if (caughtError === null) {
      return this.props.children;
    }

    // Non-chunk errors: re-throw so a parent boundary (or React's default
    // unhandled-error path) handles them. This prevents masking real regressions.
    if (!isChunkLoadError(caughtError)) {
      throw caughtError;
    }

    return (
      <div
        data-testid="chunk-load-error-boundary"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 'var(--fm-space-4)',
          fontFamily: 'sans-serif',
          color: 'var(--fm-d-fg)',
        }}
      >
        <p style={{ margin: 0, fontSize: '1.1rem' }}>
          Sovelluksen lataus epäonnistui.
        </p>
        <button
          type="button"
          onClick={() => { window.location.reload(); }}
          style={{
            padding: 'var(--fm-space-2) var(--fm-space-5)',
            fontSize: '1rem',
            cursor: 'pointer',
            borderRadius: 'var(--fm-radius-xs)',
            border: 'var(--fm-border-thin) solid var(--fm-brand)',
            backgroundColor: 'var(--fm-brand)',
            color: 'var(--fm-d-surface)',
          }}
        >
          Failed to load. Click to reload.
        </button>
      </div>
    );
  }
}

export default ChunkLoadErrorBoundary;
